import { DateTime } from "luxon";
import { SchoolClosure } from "model";

/**
 * Returns the minimum start date that can be selected so to not create any
 * School Closure overlaps.
 */
export function getMaxStartDate(params: { closures: Array<SchoolClosure>; end: string }) {
  const { closures, end } = params;

  // Find all the closured that end before the selected end date
  const closuresBeforeEnd = closures.filter(
    (closure) => DateTime.fromISO(closure.end) < DateTime.fromISO(end),
  );

  // Bail if there are no closures that end before the selected end date
  if (closuresBeforeEnd.length === 0) {
    return undefined;
  }

  // Find the latest end date which is the minimum start date
  const maxStartDate = closuresBeforeEnd.reduce((acc, curr) => {
    return DateTime.fromISO(acc) < DateTime.fromISO(curr.end) ? curr.end : acc;
  }, closuresBeforeEnd[0].end);

  return maxStartDate;
}

/**
 * @description
 * Makes sure that the admin will not be able to set overlapping
 * non-school days
 *
 * @param closures The non-school day(s) / closures
 * @param start The start time in UTC format
 *
 */
export function getMaxEndDate(closures: SchoolClosure[], start: string) {
  // get the last end date first
  const lastEndDate = closures.reduce(
    (acc, curr) => (DateTime.fromISO(acc) < DateTime.fromISO(curr.end) ? acc : curr.end),
    "",
  );

  // return early if the start date is later than the last end date
  // This allows the admin to select an end date if in the future
  if (start > lastEndDate) {
    return "";
  }

  const max = closures.reduce((acc, current) => {
    const currentTime = DateTime.fromISO(current.start);
    const startDate = DateTime.fromISO(start);

    // Only consider closures that are after the start day
    if (startDate < currentTime) {
      return DateTime.fromISO(acc) < currentTime ? acc : current.start;
    }

    return acc;
  }, "");

  return max;
}

export type AcademicYear = { startDate: DateTime; endDate: DateTime };

/**
 * Returns a tuple of objects with `startDate` and `endDate` for the
 * current and next academic years.
 */
export function currentAndNextAcademicYear(now: DateTime): Array<AcademicYear> {
  return [
    {
      startDate: DateTime.fromObject({ year: now.year, month: 7, day: 1 }),
      endDate: DateTime.fromObject({ year: now.year + 1, month: 6, day: 30 }),
    },
    {
      startDate: DateTime.fromObject({ year: now.year + 1, month: 7, day: 1 }),
      endDate: DateTime.fromObject({ year: now.year + 2, month: 6, day: 30 }),
    },
  ];
}

export function segmentClosures(closures: SchoolClosure[], start: DateTime, end: DateTime) {
  closures.filter((item) => {
    return DateTime.fromISO(item.start) > start && DateTime.fromISO(item.start) < end;
  });
}
